import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, User, ChevronRight, BookOpen } from "lucide-react";
import { useLanguage } from "../i18n/LanguageProvider";
import Seo from "../seo/Seo";
import DOMPurify from "dompurify";
import { marked } from "marked";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const formatDate = (dateStr, lang) => {
  const date = new Date(dateStr);
  const locales = { nl: "nl-NL", en: "en-GB", fr: "fr-FR", de: "de-DE" };
  return date.toLocaleDateString(locales[lang] || "nl-NL", {
    year: "numeric", month: "long", day: "numeric"
  });
};

const CTA_TEXT = {
  nl: { heading: "Hulp nodig bij dit onderwerp?", body: "Neem contact op voor een vrijblijvende offerte of stel uw vraag aan onze chatbot.", button: "Offerte Aanvragen" },
  en: { heading: "Need help with this topic?", body: "Contact us for a free quote or ask our chatbot.", button: "Request Quote" },
  fr: { heading: "Besoin d'aide sur ce sujet?", body: "Contactez-nous pour un devis gratuit ou posez votre question à notre chatbot.", button: "Demander un Devis" },
  de: { heading: "Hilfe zu diesem Thema benötigt?", body: "Kontaktieren Sie uns für ein kostenloses Angebot oder fragen Sie unseren Chatbot.", button: "Angebot Anfordern" },
};

const ArticleHero = ({ image, title }) => (
  <div className="relative h-[40vh] min-h-[300px] overflow-hidden pt-16">
    <img src={image} alt={title} className="w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
  </div>
);

const Breadcrumb = ({ title, t }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex items-center gap-2 text-sm text-[#A1A1AA] mb-6"
  >
    <Link to="/" className="hover:text-[#4DD0E1] transition-colors">{t.blog.backToHome}</Link>
    <ChevronRight size={14} />
    <Link to="/blog" className="hover:text-[#4DD0E1] transition-colors">{t.blog.badge}</Link>
    <ChevronRight size={14} />
    <span className="text-white truncate">{title}</span>
  </motion.div>
);

const ArticleHeader = ({ article, title, excerpt, language, t }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.1 }}
  >
    <span className="inline-block px-3 py-1 rounded-full bg-[#4DD0E1]/10 text-[#4DD0E1] text-xs font-medium mb-4">
      {t.blog.categories[article.category] || article.category}
    </span>
    <h1 className="text-3xl sm:text-4xl font-black text-white mb-4" data-testid="article-title">
      {title}
    </h1>
    <p className="text-lg text-[#A1A1AA] mb-6">{excerpt}</p>
    <div className="flex items-center gap-4 text-sm text-[#A1A1AA] pb-8 border-b border-white/10">
      <span className="flex items-center gap-1.5"><User size={14} /> {article.author}</span>
      <span className="flex items-center gap-1.5"><Clock size={14} /> {t.blog.publishedOn} {formatDate(article.created_at, language)}</span>
    </div>
  </motion.div>
);

const ArticleCTA = ({ language }) => {
  const cta = CTA_TEXT[language] || CTA_TEXT.nl;
  return (
    <div className="py-8 border-t border-white/10">
      <div className="p-6 rounded-xl bg-gradient-to-r from-[#4DD0E1]/10 to-transparent border border-[#4DD0E1]/20">
        <p className="text-white font-semibold mb-2">{cta.heading}</p>
        <p className="text-sm text-[#A1A1AA] mb-4">{cta.body}</p>
        <Link
          to="/#offerte"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#4DD0E1] text-black text-sm font-bold hover:bg-[#3bc0d1] transition-colors"
          data-testid="article-cta"
        >
          {cta.button} <ChevronRight size={16} />
        </Link>
      </div>
    </div>
  );
};

const RelatedArticles = ({ related, language, t }) => {
  if (related.length === 0) return null;
  return (
    <div className="py-10 border-t border-white/10">
      <h3 className="text-xl font-bold text-white mb-6">{t.blog.relatedArticles}</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        {related.map(rel => (
          <Link
            key={rel.slug}
            to={`/blog/${rel.slug}`}
            className="group flex gap-4 p-4 rounded-xl bg-[#121212] border border-white/10 hover:border-[#4DD0E1]/30 transition-colors"
            data-testid={`related-${rel.slug}`}
          >
            <img src={rel.image} alt={rel.title[language] || rel.title.nl} className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
            <div className="min-w-0">
              <h4 className="text-sm font-semibold text-white group-hover:text-[#4DD0E1] transition-colors line-clamp-2">
                {rel.title[language] || rel.title.nl}
              </h4>
              <span className="text-xs text-[#A1A1AA] mt-1 block">{formatDate(rel.created_at, language)}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default function BlogArticle() {
  const { slug } = useParams();
  const { language, t } = useLanguage();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/blog/${slug}`),
      axios.get(`${API}/blog?lang=${language}`)
    ]).then(([articleRes, allRes]) => {
      setArticle(articleRes.data);
      setRelated(
        allRes.data
          .filter(a => a.slug !== slug && a.category === articleRes.data.category)
          .slice(0, 2)
      );
    }).catch(() => {
      setArticle(null);
    }).finally(() => setLoading(false));
  }, [slug, language]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#4DD0E1] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <BookOpen size={48} className="mx-auto mb-4 text-[#A1A1AA] opacity-40" />
          <p className="text-[#A1A1AA] mb-4">Artikel niet gevonden</p>
          <Link to="/blog" className="text-[#4DD0E1] hover:underline">{t.blog.backToBlog}</Link>
        </div>
      </div>
    );
  }

  const title = article.title[language] || article.title.nl;
  const content = article.content[language] || article.content.nl;
  const excerpt = article.excerpt[language] || article.excerpt.nl;
  const htmlContent = DOMPurify.sanitize(marked.parse(content));

  const articleTitle = `${title} | Clean-Force Blog`;
  const publishedTime = article.created_at ? new Date(article.created_at).toISOString() : undefined;
  const modifiedTime = article.updated_at ? new Date(article.updated_at).toISOString() : publishedTime;

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="blog-article-page">
      <Seo
        path={`/blog/${slug}`}
        lang={language}
        title={articleTitle}
        description={excerpt}
        image={article.image}
        ogType="article"
        articleMeta={{ published: publishedTime, modified: modifiedTime, author: article.author }}
      />
      <ArticleHero image={article.image} title={title} />
      <div className="max-w-3xl mx-auto px-6 -mt-24 relative z-10">
        <Breadcrumb title={title} t={t} />
        <ArticleHeader article={article} title={title} excerpt={excerpt} language={language} t={t} />
        <motion.article
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="prose-cf py-10"
          data-testid="article-content"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
        <ArticleCTA language={language} />
        <RelatedArticles related={related} language={language} t={t} />
        <div className="py-8 text-center">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[#4DD0E1] hover:underline" data-testid="back-to-blog">
            <ArrowLeft size={16} /> {t.blog.backToBlog}
          </Link>
        </div>
      </div>
    </div>
  );
}
